import { isInitializedImageElement } from "@excalidraw/element";
import { randomId } from "@excalidraw/common";

import type { FileId } from "@excalidraw/element/types";
import type { BinaryFileData, BinaryFiles } from "@excalidraw/excalidraw/types";

import { LocalData } from "../data/LocalData";

import { documentStorage } from "./documentStorage";

import type { DocumentMeta, StoredDocumentData } from "./documentStorage";

const cloudURL =
  import.meta.env.VITE_APP_STORAGE_BACKEND_URL ||
  "http://localhost:8787/api/v2";

type CloudDocument = DocumentMeta & { data: StoredDocumentData };

export class CloudDocumentError extends Error {
  constructor(message: string, readonly status: number, readonly body?: any) {
    super(message);
  }
}

let cloudUserId: string | null = null;
const uploadedFiles = new Set<string>();

export const setCloudUser = (userId: string | null) => {
  cloudUserId = userId;
  uploadedFiles.clear();
};

export const hasCloudUser = (expectedUserId?: string) =>
  !!cloudUserId && (!expectedUserId || cloudUserId === expectedUserId);

const request = async <T>(
  path: string,
  init?: RequestInit,
  expectedUserId = cloudUserId,
): Promise<T> => {
  if (cloudUserId !== expectedUserId) {
    throw new CloudDocumentError("Account session changed", 409);
  }
  const response = await fetch(`${cloudURL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : undefined),
      ...(expectedUserId ? { "x-excali-user-id": expectedUserId } : undefined),
      ...init?.headers,
    },
  });
  const body = response.status === 204 ? null : await response.json();
  if (cloudUserId !== expectedUserId) {
    throw new CloudDocumentError("Account session changed", 409);
  }
  if (!response.ok) {
    throw new CloudDocumentError(
      body?.error || `Cloud request failed (${response.status})`,
      response.status,
      body,
    );
  }
  return body as T;
};

const isDirty = (meta: DocumentMeta) =>
  meta.syncState === "saving" ||
  meta.syncState === "offline" ||
  meta.syncState === "conflict";

const recoverCachedDocument = async (
  meta: DocumentMeta,
  data: StoredDocumentData,
  userId: string,
) => {
  const now = Date.now();
  const recovered: DocumentMeta = {
    id: randomId(),
    name: `${meta.name} (recovered copy)`,
    createdAt: now,
    updatedAt: now,
    syncState: "offline",
  };
  await documentStorage.saveDocument(recovered.id, data, userId);
  await documentStorage.deleteDocument(meta.id, userId);
  if (cloudUserId !== userId) {
    return recovered;
  }
  try {
    return await createCloudDocument(recovered, data);
  } catch (error) {
    console.error(error);
    return recovered;
  }
};

const getFileIds = (data: StoredDocumentData): FileId[] =>
  data.elements.reduce((ids, element) => {
    if (isInitializedImageElement(element)) {
      ids.push(element.fileId);
    }
    return ids;
  }, [] as FileId[]);

const collectCloudDocumentFiles = async (
  documentId: string,
  data: StoredDocumentData,
  suppliedFiles?: BinaryFiles,
) => {
  const fileIds = getFileIds(data);
  const missingIds = fileIds.filter(
    (fileId) => !uploadedFiles.has(`${documentId}:${fileId}`),
  );
  if (!missingIds.length) {
    return { files: {}, uploadedKeys: [] as string[] };
  }

  const localFiles = await LocalData.fileStorage.getFiles(missingIds);
  const files = new Map<string, BinaryFileData>();
  localFiles.loadedFiles.forEach((file) => files.set(file.id, file));
  Object.values(suppliedFiles || {}).forEach((file) =>
    files.set(file.id, file),
  );
  const unavailableIds = missingIds.filter((fileId) => !files.has(fileId));
  if (unavailableIds.length) {
    throw new Error(
      `Missing ${unavailableIds.length} local file${
        unavailableIds.length === 1 ? "" : "s"
      }`,
    );
  }

  return {
    files: Object.fromEntries(
      missingIds.map((fileId) => [fileId, files.get(fileId)!]),
    ),
    uploadedKeys: missingIds.map((fileId) => `${documentId}:${fileId}`),
  };
};

const downloadFiles = async (documentId: string, data: StoredDocumentData) => {
  const fileIds = getFileIds(data);
  if (!fileIds.length) {
    return;
  }
  const files = (
    await Promise.all(
      fileIds.map(async (fileId) => {
        try {
          const file = await request<BinaryFileData>(
            `/documents/${documentId}/files/${fileId}`,
          );
          uploadedFiles.add(`${documentId}:${fileId}`);
          return file;
        } catch (error) {
          if (error instanceof CloudDocumentError && error.status === 404) {
            return null;
          }
          throw error;
        }
      }),
    )
  ).filter((file): file is BinaryFileData => !!file);

  if (files.length) {
    const binaryFiles = Object.fromEntries(
      files.map((file) => [file.id, file]),
    ) as BinaryFiles;
    await LocalData.fileStorage.saveFiles({
      elements: data.elements,
      files: binaryFiles,
    });
  }
};

export const hydrateCloudDocuments = async (userId: string) => {
  setCloudUser(userId);
  documentStorage.setScope(userId);

  const cachedIndex = await documentStorage.loadIndex(userId);
  let remoteIndex: DocumentMeta[];
  try {
    const result = await request<{
      userId: string;
      documents: DocumentMeta[];
    }>("/documents", undefined, userId);
    if (result.userId !== userId || cloudUserId !== userId) {
      return;
    }
    remoteIndex = result.documents;
  } catch (error) {
    await documentStorage.saveIndex(
      cachedIndex.map((meta) => ({ ...meta, syncState: "offline" })),
      userId,
    );
    return;
  }

  if (!remoteIndex.length) {
    const recovered: DocumentMeta[] = [];
    for (const cachedMeta of cachedIndex) {
      const cachedData = await documentStorage.loadDocument(
        cachedMeta.id,
        userId,
      );
      if (cachedData && isDirty(cachedMeta)) {
        recovered.push(
          await recoverCachedDocument(cachedMeta, cachedData, userId),
        );
      } else {
        await documentStorage.deleteDocument(cachedMeta.id, userId);
      }
    }
    if (recovered.length) {
      await documentStorage.saveIndex(recovered, userId);
      return;
    }
    const guestIndex = await documentStorage.loadIndex("guest");
    if (
      guestIndex.length &&
      window.confirm(
        `Save your ${guestIndex.length} local drawing${
          guestIndex.length === 1 ? "" : "s"
        } to this account?`,
      )
    ) {
      const imported = await importGuestDocuments();
      await documentStorage.saveIndex([...cachedIndex, ...imported], userId);
      return;
    }
    await documentStorage.saveIndex([], userId);
    return;
  }

  const nextIndex: DocumentMeta[] = [];
  for (const remoteMeta of remoteIndex) {
    const cachedMeta = cachedIndex.find((meta) => meta.id === remoteMeta.id);
    const cachedData = await documentStorage.loadDocument(
      remoteMeta.id,
      userId,
    );
    if (cachedMeta && cachedData && isDirty(cachedMeta)) {
      if (cachedMeta.revision === remoteMeta.revision) {
        try {
          nextIndex.push(await saveCloudDocument(cachedMeta, cachedData));
        } catch (error) {
          nextIndex.push({ ...cachedMeta, syncState: "offline" });
          console.error(error);
        }
        continue;
      }
      nextIndex.push(
        await recoverCachedDocument(cachedMeta, cachedData, userId),
      );
    }
    if (cloudUserId !== userId) {
      return;
    }
    let remote: CloudDocument;
    try {
      remote = await request<CloudDocument>(`/documents/${remoteMeta.id}`);
    } catch (error) {
      if (cachedData && cachedMeta) {
        nextIndex.push({ ...cachedMeta, syncState: "offline" });
        console.error(error);
        continue;
      }
      throw error;
    }
    if (cloudUserId !== userId) {
      return;
    }
    const data = remote.data;
    try {
      await documentStorage.saveDocument(remote.id, data, userId);
      await downloadFiles(remote.id, data);
    } catch (error) {
      if (!cachedData || !cachedMeta) {
        throw error;
      }
      await documentStorage.saveDocument(remote.id, cachedData, userId);
      nextIndex.push({
        ...cachedMeta,
        syncState: "offline",
      });
      console.error(error);
      continue;
    }
    nextIndex.push({
      id: remote.id,
      name: remote.name,
      revision: remote.revision,
      createdAt: remote.createdAt,
      updatedAt: remote.updatedAt,
      syncState: "saved",
    });
  }

  for (const cachedMeta of cachedIndex) {
    if (nextIndex.some((meta) => meta.id === cachedMeta.id)) {
      continue;
    }
    if (!cachedMeta.revision || isDirty(cachedMeta)) {
      const data = await documentStorage.loadDocument(cachedMeta.id, userId);
      if (data) {
        nextIndex.push(
          cachedMeta.revision
            ? await recoverCachedDocument(cachedMeta, data, userId)
            : await createCloudDocument(cachedMeta, data),
        );
      }
    } else {
      await documentStorage.deleteDocument(cachedMeta.id, userId);
    }
  }
  await documentStorage.saveIndex(nextIndex, userId);
};

export const createCloudDocument = async (
  meta: DocumentMeta,
  data: StoredDocumentData,
  suppliedFiles?: BinaryFiles,
): Promise<DocumentMeta> => {
  const userId = cloudUserId;
  if (!userId) {
    return { ...meta, syncState: "local" };
  }
  const pendingFiles = await collectCloudDocumentFiles(
    meta.id,
    data,
    suppliedFiles,
  );
  const remote = await request<CloudDocument>(
    "/documents",
    {
      method: "POST",
      body: JSON.stringify({
        id: meta.id,
        name: meta.name,
        data,
        files: pendingFiles.files,
      }),
    },
    userId,
  );
  pendingFiles.uploadedKeys.forEach((key) => uploadedFiles.add(key));
  return {
    id: remote.id,
    name: remote.name,
    revision: remote.revision,
    createdAt: remote.createdAt,
    updatedAt: remote.updatedAt,
    syncState: "saved",
  };
};

export const saveCloudDocument = async (
  meta: DocumentMeta,
  data: StoredDocumentData,
  suppliedFiles?: BinaryFiles,
): Promise<DocumentMeta> => {
  const userId = cloudUserId;
  if (!userId) {
    return { ...meta, syncState: "local" };
  }
  if (!meta.revision) {
    return createCloudDocument(meta, data, suppliedFiles);
  }
  const pendingFiles = await collectCloudDocumentFiles(
    meta.id,
    data,
    suppliedFiles,
  );
  const remote = await request<CloudDocument>(
    `/documents/${meta.id}`,
    {
      method: "PUT",
      body: JSON.stringify({
        name: meta.name,
        data,
        expectedRevision: meta.revision,
        files: pendingFiles.files,
      }),
    },
    userId,
  );
  pendingFiles.uploadedKeys.forEach((key) => uploadedFiles.add(key));
  // Updates remove unreferenced files on the server. Forget those uploads so
  // undoing an image deletion sends its bytes again.
  const referencedKeys = new Set(
    getFileIds(data).map((fileId) => `${meta.id}:${fileId}`),
  );
  for (const key of uploadedFiles) {
    if (key.startsWith(`${meta.id}:`) && !referencedKeys.has(key)) {
      uploadedFiles.delete(key);
    }
  }
  return {
    id: remote.id,
    name: remote.name,
    revision: remote.revision,
    createdAt: remote.createdAt,
    updatedAt: remote.updatedAt,
    syncState: "saved",
  };
};

export const importGuestDocuments = async (): Promise<DocumentMeta[]> => {
  if (!cloudUserId) {
    return [];
  }
  const userId = cloudUserId;
  const guestIndex = await documentStorage.loadIndex("guest");
  const accountIndex = await documentStorage.loadIndex(userId);
  const imported: DocumentMeta[] = [];

  for (const meta of guestIndex) {
    if (cloudUserId !== userId) {
      break;
    }
    if (accountIndex.some((accountMeta) => accountMeta.id === meta.id)) {
      continue;
    }
    const data = await documentStorage.loadDocument(meta.id, "guest");
    if (!data) {
      continue;
    }
    const remote = await createCloudDocument(meta, data);
    await documentStorage.saveDocument(meta.id, data, userId);
    imported.push(remote);
  }
  return imported;
};

export const deleteCloudDocument = async (id: string, revision?: number) => {
  const userId = cloudUserId;
  if (userId) {
    const expectedRevision =
      revision ||
      (await request<CloudDocument>(`/documents/${id}`, undefined, userId))
        .revision;
    await request(
      `/documents/${id}?revision=${expectedRevision}`,
      { method: "DELETE" },
      userId,
    );
  }
};
